import { create } from 'zustand';
import type {
  PlatformUser,
  Organization,
  Project,
  Environment,
  UsageSummary,
  AuditLogEntry,
  AiUsage,
  LogEntry,
  ResourceMetrics,
  PlanTier,
  PLAN_LIMITS as PlanLimitsMap,
} from '@/types/platform';
import { PLAN_LIMITS } from '@/types/platform';

/* ================================================================== */
/*  Platform Context Store                                            */
/*                                                                    */
/*  The single source of truth for "who am I, where am I, what can I  */
/*  do?" Tracks user, org, project, environment — the governance      */
/*  backbone of the sovereign developer cloud.                        */
/* ================================================================== */

interface PlatformState {
  /* -- Identity ---------------------------------------------------- */
  user: PlatformUser | null;
  isAuthenticated: boolean;

  /* -- Organization ------------------------------------------------ */
  activeOrg: Organization | null;

  /* -- Project context --------------------------------------------- */
  projects: Project[];
  activeProject: Project | null;
  activeEnvironment: Environment | null;

  /* -- Usage & limits ---------------------------------------------- */
  usage: UsageSummary | null;
  aiUsage: AiUsage | null;

  /* -- Audit & logs ------------------------------------------------ */
  auditLog: AuditLogEntry[];
  logs: LogEntry[];
  isStreamingLogs: boolean;

  /* -- Resource metrics -------------------------------------------- */
  metrics: ResourceMetrics | null;

  /* -- Loading states ---------------------------------------------- */
  isLoading: boolean;

  /* -- Actions: Identity ------------------------------------------- */
  setUser: (user: PlatformUser) => void;
  clearUser: () => void;
  switchOrg: (orgId: string) => void;

  /* -- Actions: Project -------------------------------------------- */
  setProjects: (projects: Project[]) => void;
  setActiveProject: (project: Project | null) => void;
  setActiveEnvironment: (env: Environment | null) => void;
  addProject: (project: Project) => void;
  removeProject: (projectId: string) => void;

  /* -- Actions: Usage ---------------------------------------------- */
  setUsage: (usage: UsageSummary) => void;
  setAiUsage: (usage: AiUsage) => void;

  /* -- Actions: Audit ---------------------------------------------- */
  setAuditLog: (entries: AuditLogEntry[]) => void;
  appendAuditEntry: (entry: AuditLogEntry) => void;

  /* -- Actions: Logs ----------------------------------------------- */
  setLogs: (logs: LogEntry[]) => void;
  appendLog: (log: LogEntry) => void;
  clearLogs: () => void;
  setStreamingLogs: (streaming: boolean) => void;

  /* -- Actions: Metrics -------------------------------------------- */
  setMetrics: (metrics: ResourceMetrics) => void;

  /* -- Computed helpers --------------------------------------------- */
  getPlanLimits: () => (typeof PlanLimitsMap)[PlanTier] | null;
  getUsagePercent: (resource: keyof UsageSummary) => number;
  isOverLimit: (resource: keyof UsageSummary) => boolean;
}

export const usePlatformStore = create<PlatformState>((set, get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  activeOrg: null,
  projects: [],
  activeProject: null,
  activeEnvironment: null,
  usage: null,
  aiUsage: null,
  auditLog: [],
  logs: [],
  isStreamingLogs: false,
  metrics: null,
  isLoading: false,

  // Identity
  setUser: (user) => {
    const activeOrg = user.organizations.length > 0
      ? {
          id: user.organizations[0].id,
          name: user.organizations[0].name,
          slug: user.organizations[0].slug,
          plan: 'free' as PlanTier,
          ownerId: user.id,
          members: [],
          createdAt: '',
          settings: {
            defaultChainId: 11155111,
            aiModel: 'auto' as const,
            deploymentApproval: 'auto' as const,
            auditLogRetentionDays: 30,
          },
          usage: {
            computeHours: 0, computeLimit: 50,
            storageGb: 0, storageLimit: 1,
            rpcRequests: 0, rpcLimit: 10_000,
            aiTokens: 0, aiTokenLimit: 50_000,
            deployments: 0, deploymentsLimit: 20,
            periodStart: '', periodEnd: '',
          },
        }
      : null;
    set({ user, isAuthenticated: true, activeOrg });
  },

  clearUser: () => set({
    user: null,
    isAuthenticated: false,
    activeOrg: null,
    projects: [],
    activeProject: null,
    activeEnvironment: null,
    usage: null,
    aiUsage: null,
    auditLog: [],
    logs: [],
    metrics: null,
    isStreamingLogs: false,
    isLoading: false,
  }),

  switchOrg: (orgId) => {
    const user = get().user;
    if (!user) return;
    const orgRef = user.organizations.find((o) => o.id === orgId);
    if (!orgRef) return;
    set({
      activeOrg: {
        id: orgRef.id,
        name: orgRef.name,
        slug: orgRef.slug,
        plan: 'free',
        ownerId: user.id,
        members: [],
        createdAt: '',
        settings: {
          defaultChainId: 11155111,
          aiModel: 'auto',
          deploymentApproval: 'auto',
          auditLogRetentionDays: 30,
        },
        usage: get().usage ?? {
          computeHours: 0, computeLimit: 50,
          storageGb: 0, storageLimit: 1,
          rpcRequests: 0, rpcLimit: 10_000,
          aiTokens: 0, aiTokenLimit: 50_000,
          deployments: 0, deploymentsLimit: 20,
          periodStart: '', periodEnd: '',
        },
      },
      projects: [],
      activeProject: null,
      activeEnvironment: null,
    });
  },

  // Projects
  setProjects: (projects) => set({ projects }),

  setActiveProject: (project) => {
    const env = project?.environments.find((e) => e.isDefault) ?? project?.environments[0] ?? null;
    set({ activeProject: project, activeEnvironment: env });
  },

  setActiveEnvironment: (env) => set({ activeEnvironment: env }),

  addProject: (project) => set((s) => ({ projects: [...s.projects, project] })),

  removeProject: (projectId) => set((s) => ({
    projects: s.projects.filter((p) => p.id !== projectId),
    activeProject: s.activeProject?.id === projectId ? null : s.activeProject,
    activeEnvironment: s.activeProject?.id === projectId ? null : s.activeEnvironment,
  })),

  // Usage
  setUsage: (usage) => set({ usage }),
  setAiUsage: (usage) => set({ aiUsage: usage }),

  // Audit
  setAuditLog: (entries) => set({ auditLog: entries }),
  appendAuditEntry: (entry) => set((s) => ({
    auditLog: [entry, ...s.auditLog].slice(0, 500),
  })),

  // Logs
  setLogs: (logs) => set({ logs }),
  appendLog: (log) => set((s) => ({
    logs: [...s.logs, log].slice(-2000), // Keep last 2000
  })),
  clearLogs: () => set({ logs: [] }),
  setStreamingLogs: (streaming) => set({ isStreamingLogs: streaming }),

  // Metrics
  setMetrics: (metrics) => set({ metrics }),

  // Computed
  getPlanLimits: () => {
    const org = get().activeOrg;
    if (!org) return null;
    return PLAN_LIMITS[org.plan];
  },

  getUsagePercent: (resource) => {
    const usage = get().usage;
    if (!usage) return 0;
    const value = usage[resource];
    const limitKey = `${String(resource)}Limit` as keyof UsageSummary;
    const limit = usage[limitKey];
    if (typeof value !== 'number' || typeof limit !== 'number' || limit <= 0) return 0;
    return Math.min(100, Math.round((value / limit) * 100));
  },

  isOverLimit: (resource) => {
    return get().getUsagePercent(resource) >= 100;
  },
}));
